import React, { useMemo, useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import Button from '../ui/Button';

export const getPasswordStrength = (password = '') => {
  if (!password) {
    return { score: 0, label: 'Very weak' };
  }

  let score = Math.min(70, password.length * 6);
  if (/[A-Z]/.test(password)) score += 8;
  if (/[a-z]/.test(password)) score += 8;
  if (/[0-9]/.test(password)) score += 8;
  if (/[^A-Za-z0-9]/.test(password)) score += 6;

  score = Math.max(0, Math.min(100, score));

  if (score < 30) return { score, label: 'Very weak' };
  if (score < 55) return { score, label: 'Weak' };
  if (score < 75) return { score, label: 'Okay' };
  return { score, label: 'Strong' };
};

export const getPasswordFailures = (
  password = '',
  { minLength = 8, enforceCompositionRules = true } = {}
) => {
  const failures = [];

  if (password.length < minLength) {
    failures.push(`Password must be at least ${minLength} characters`);
  }

  if (enforceCompositionRules) {
    if (!/[A-Z]/.test(password)) failures.push('Include at least one uppercase letter');
    if (!/[a-z]/.test(password)) failures.push('Include at least one lowercase letter');
    if (!/[0-9]/.test(password)) failures.push('Include at least one number');
  }

  return failures;
};

export const validatePassword = (
  password = '',
  minLength = 8,
  enforceCompositionRules = true
) => {
  const failures = getPasswordFailures(password, { minLength, enforceCompositionRules });
  return failures[0] || '';
};

const PasswordField = ({
  id = 'password',
  name,
  label = 'Password',
  description = '',
  value,
  onChange,
  onBlur,
  error = '',
  minLength = 8,
  recommendedLength = 12,
  enforceCompositionRules = true,
  required = true,
  showHints = false,
  placeholder = '••••••••',
  autoComplete = 'new-password',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [tipsExpanded, setTipsExpanded] = useState(false);

  const strength = useMemo(() => getPasswordStrength(value), [value]);
  const guidanceLines = useMemo(() => {
    const lines = [
      `Use ${recommendedLength}+ characters whenever possible.`,
      `Minimum required is ${minLength} characters.`
    ];

    if (enforceCompositionRules) {
      lines.push('Use uppercase, lowercase, and at least one number.');
    }

    lines.push('Use a unique password you do not reuse elsewhere.');
    return lines;
  }, [recommendedLength, minLength, enforceCompositionRules]);

  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const strengthId = `${id}-strength`;
  const tipsId = `${id}-tips`;

  const describedBy = [];
  if (showHints) describedBy.push(strengthId);
  if (error) describedBy.push(errorId);
  if (showHints && !error) describedBy.push(helperId);
  if (showHints && tipsExpanded) describedBy.push(tipsId);

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium text-[var(--color-text-secondary)]">
        {label}
      </label>
      {description ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          {description}
        </p>
      ) : null}

      <div className="relative">
        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
        <input
          id={id}
          name={name || id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete={autoComplete}
          maxLength={64}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy.join(' ')}
          className={`h-12 w-full rounded-xl border bg-[var(--color-surface-input)] px-10 pr-12 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500/40 ${
            error ? 'border-rose-500/60' : 'border-[var(--color-border)]'
          }`}
        />
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          onClick={() => setShowPassword((current) => !current)}
          className="absolute inset-y-0 right-1.5 my-auto"
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>

      {showHints && (
        <div className="flex items-center gap-2 text-xs">
          <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-hover)]">
            <div
              className={`h-full transition-all duration-200 ${
                strength.score < 30
                  ? 'auth-meter-danger'
                  : strength.score < 55
                    ? 'auth-meter-warning'
                    : strength.score < 75
                      ? 'auth-meter-warning'
                      : 'auth-meter-success'
              }`}
              style={{ width: `${Math.max(strength.score, 4)}%` }}
            />
          </div>
          <span id={strengthId} className="whitespace-nowrap text-[var(--color-text-muted)]" aria-live="polite">
            Strength: {strength.label}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto min-h-0 whitespace-nowrap rounded-md p-1 text-xs font-medium underline-offset-4 hover:underline"
            onClick={() => setTipsExpanded((current) => !current)}
            aria-expanded={tipsExpanded}
            aria-controls={tipsId}
          >
            Tips
          </Button>
        </div>
      )}

      {showHints && !error && (
        <p id={helperId} className="text-xs text-[var(--color-text-muted)]">
          Must be at least {minLength} characters.
        </p>
      )}

      {showHints && tipsExpanded && (
        <ul id={tipsId} className="list-disc space-y-1 pl-4 text-xs text-[var(--color-text-secondary)]">
          {guidanceLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      {error && (
        <p id={errorId} role="alert" aria-live="assertive" className="text-xs auth-danger">
          {error}
        </p>
      )}
    </div>
  );
};

export default PasswordField;
